import {
  AlertTriangle,
  Info,
  Mail,
  Phone,
  Search,
  ShieldAlert,
  X,
} from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { DetailedMatter } from '@/services/types/client-intake-types';

import { useClientCommunications } from './ClientCommunicationsContext';

interface ClientCommunicationsHeaderProps {
  matter: DetailedMatter;
  clientPhone?: string;
  clientEmail?: string;
  doNotContact?: boolean;
  onOpenEmailComposer: () => void;
  onInitiateCall: () => void;
}

export const ClientCommunicationsHeader: React.FC<
  ClientCommunicationsHeaderProps
> = ({
  matter,
  clientPhone,
  clientEmail,
  doNotContact,
  onOpenEmailComposer,
  onInitiateCall,
}) => {
  const { searchQuery, setSearchQuery } = useClientCommunications();

  const [isSearchOpen, setIsSearchOpen] = useState(!!searchQuery);
  const clientName =
    matter.clients?.[0]?.name || matter.name || 'Unknown Client';

  const handleSearchToggle = () => {
    if (isSearchOpen && searchQuery) {
      setSearchQuery('');
    }
    setIsSearchOpen(!isSearchOpen);
  };

  const getEmailTooltip = () => {
    if (doNotContact) return 'Contact disabled for this matter';
    if (clientEmail) return 'Send email';
    return 'No email on file';
  };

  const getCallTooltip = () => {
    if (doNotContact) return 'Contact disabled for this matter';
    if (clientPhone) return 'Call client';
    return 'No phone on file';
  };

  return (
    <div className="space-y-0">
      {/* DNC Warning Bar */}
      {doNotContact && (
        <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold bg-red-50 text-red-700 border-b border-red-100">
          <ShieldAlert className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            <span className="font-bold">Do Not Contact</span> — This matter is
            marked as do not contact client
          </span>
        </div>
      )}

      <div className="flex items-center justify-between px-3 py-2">
        {/* Left: Client name and firm */}
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <h2 className="text-sm font-bold text-zinc-900 truncate">
              {clientName}
            </h2>

            {/* Client Info Popover */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="p-0.5 rounded-full hover:bg-zinc-200 transition-colors text-zinc-400 hover:text-zinc-600"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="start"
                className="w-auto min-w-[180px] p-2 shadow-lg"
              >
                <div className="space-y-2">
                  {/* Phone */}
                  <div className="flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                    <span
                      className={cn(
                        'text-xs font-medium',
                        clientPhone ? 'text-zinc-700' : 'text-zinc-400 italic',
                      )}
                    >
                      {clientPhone || 'No phone'}
                    </span>
                  </div>

                  {/* Email */}
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                    <span
                      className={cn(
                        'text-xs font-medium',
                        clientEmail ? 'text-zinc-700' : 'text-zinc-400 italic',
                      )}
                    >
                      {clientEmail || 'No email'}
                    </span>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {matter.firm?.name && (
            <div className="flex items-center gap-1">
              <p className="text-[11px] text-zinc-700 font-bold truncate">
                on behalf of{' '}
                <span className="text-primary">{matter.firm.name}</span>
              </p>
              {!matter.firm.sms_sender_number && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={`/firms/${matter.firm.id}`}
                      className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors"
                    >
                      <AlertTriangle className="h-2.5 w-2.5" />
                      SMS
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[200px]">
                    <p className="text-xs">
                      SMS sending is not configured for this firm. Click to
                      configure the SMS Sender Number in firm settings.
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1">
          {/* Search */}
          <div
            className={cn(
              'flex items-center transition-all duration-200',
              isSearchOpen ? 'w-48' : 'w-8',
            )}
          >
            {isSearchOpen ? (
              <div className="relative w-full">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="h-8 pl-7 pr-7 text-sm"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={handleSearchToggle}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={handleSearchToggle}
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Search</TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Email */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn('h-8 w-8', doNotContact && 'opacity-50')}
                onClick={onOpenEmailComposer}
                disabled={!clientEmail || doNotContact}
              >
                <Mail className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{getEmailTooltip()}</TooltipContent>
          </Tooltip>

          {/* Call */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn('h-8 w-8', doNotContact && 'opacity-50')}
                onClick={onInitiateCall}
                disabled={!clientPhone || doNotContact}
              >
                <Phone className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{getCallTooltip()}</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default ClientCommunicationsHeader;
